import { useState, useEffect, useRef } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import Hero from './components/Hero';
import Section from './components/Section';
import Footer from './components/Footer';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Contact from './components/Contact';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Programs from './components/Programs';
import Questionnaire from './components/Questionnaire';
import ImageModal from './components/ImageModal';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import FirstLoginPasswordChange from './components/FirstLoginPasswordChange';
import LearningPhotos from './components/LearningPhotos';
import { supabase } from './lib/supabase';

type AppRoute = 'home' | 'login' | 'dashboard' | 'first-login-password' | 'photos' | 'forgot-password' | 'reset-password';

function getAuthRouteFromPath(): 'forgot-password' | 'reset-password' | null {
  if (window.location.pathname === '/forgot-password') return 'forgot-password';
  if (window.location.pathname === '/reset-password') return 'reset-password';
  return null;
}

const App: React.FC = () => {
  const [route, setRoute] = useState<AppRoute>(() => getAuthRouteFromPath() || 'home');
  const routeRef = useRef(route);
  routeRef.current = route;
  const [userRole, setUserRole] = useState<'admin' | 'parent' | undefined>(undefined);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [recoverySessionReady, setRecoverySessionReady] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    let active = true;
    let sessionCheck = 0;

    const syncSession = async (session: Session | null) => {
      const check = ++sessionCheck;
      if (!session) {
        setAuthUser(null);
        setUserRole(undefined);
        setPasswordChangeRequired(false);
        setProfileName(null);
        setAuthLoading(false);
        setRoute((current) => current === 'dashboard' || current === 'first-login-password' ? 'home' : current);
        return;
      }

      setAuthLoading(true);
      try {
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('full_name, role, is_active, password_change_required')
          .eq('id', session.user.id)
          .maybeSingle();

        if (!active || check !== sessionCheck) return;
        if (error) throw new Error(`Unable to load your ETA profile: ${error.message}`);
        if (!profile || profile.is_active !== true || (profile.role !== 'admin' && profile.role !== 'parent')) {
          throw new Error('This account does not have an active ETA profile. Please contact an administrator.');
        }

        setAuthUser(session.user);
        setUserRole(profile.role);
        setPasswordChangeRequired(profile.password_change_required === true);
        setProfileName(profile.full_name);
        setAuthError(null);
        setRoute(profile.password_change_required === true ? 'first-login-password' : 'dashboard');
      } catch (error) {
        if (!active || check !== sessionCheck) return;
        setAuthUser(null);
        setUserRole(undefined);
        setPasswordChangeRequired(false);
        setProfileName(null);
        setAuthError(error instanceof Error ? error.message : 'Unable to verify your ETA profile.');
        setRoute('login');
        const { error: signOutError } = await supabase.auth.signOut();
        if (signOutError) {
          setAuthError(`${error instanceof Error ? error.message : 'Unable to verify your ETA profile.'} Sign-out failed: ${signOutError.message}`);
        }
      } finally {
        if (active && check === sessionCheck) setAuthLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      window.setTimeout(() => {
        if (!active) return;
        if (event === 'PASSWORD_RECOVERY') {
          setRecoverySessionReady(Boolean(session));
          setAuthUser(session?.user ?? null);
          setAuthLoading(false);
          setRoute('reset-password');
          if (session) {
            void supabase
              .from('profiles')
              .select('full_name, role, is_active, password_change_required')
              .eq('id', session.user.id)
              .maybeSingle()
              .then(({ data: profile, error }) => {
                if (!active) return;
                if (error || !profile || profile.is_active !== true) {
                  setAuthError('Unable to verify your ETA profile. Please sign in again.');
                  return;
                }
                setUserRole(profile.role === 'admin' || profile.role === 'parent' ? profile.role : undefined);
                setPasswordChangeRequired(profile.password_change_required === true);
                setProfileName(profile.full_name);
              });
          }
          return;
        }
        if (routeRef.current === 'reset-password') {
          setAuthUser(session?.user ?? null);
          setAuthLoading(false);
          return;
        }
        void syncSession(session);
      }, 0);
    });

    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setAuthError(`Unable to restore your Supabase session: ${error.message}`);
        setAuthLoading(false);
        return;
      }
      if (routeRef.current === 'reset-password') {
        setAuthUser(data.session?.user ?? null);
        setAuthLoading(false);
        if (data.session) {
          void supabase
            .from('profiles')
            .select('full_name, role, is_active, password_change_required')
            .eq('id', data.session.user.id)
            .maybeSingle()
            .then(({ data: profile, error }) => {
              if (!active || error || !profile || profile.is_active !== true) return;
              setUserRole(profile.role === 'admin' || profile.role === 'parent' ? profile.role : undefined);
              setPasswordChangeRequired(profile.password_change_required === true);
              setProfileName(profile.full_name);
            });
        }
      } else {
        void syncSession(data.session);
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    document.body.dataset.route = route;
  }, [route]);

  useEffect(() => {
    if (route !== 'home' || !pendingAnchor) return;
    let firstFrame = 0;
    let secondFrame = 0;
    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        if (pendingAnchor === 'home') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          const element = document.getElementById(pendingAnchor);
          if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          else window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        setPendingAnchor(null);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [pendingAnchor, route]);

  useEffect(() => {
    if (route === 'photos') {
      setActiveSection('photos');
      return;
    }
    if (route === 'login' || route === 'dashboard' || route === 'forgot-password' || route === 'reset-password') {
      setActiveSection('login');
      return;
    }

    let frame = 0;
    const updateActiveSection = () => {
      const threshold = 140;
      let active = 'home';
      ['programs', 'testimonials', 'faq', 'contact', 'location'].forEach((id) => {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= threshold) {
          active = id === 'location' ? 'contact' : id;
        }
      });
      setActiveSection((current) => current === active ? current : active);
    };
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [route]);

  useEffect(() => {
    if (!authLoading && route === 'dashboard' && (!authUser || !userRole)) setRoute('login');
    else if (!authLoading && route === 'dashboard' && passwordChangeRequired) setRoute('first-login-password');
    else if (!authLoading && route === 'first-login-password' && (!authUser || !userRole)) setRoute('login');
    else if (!authLoading && route === 'first-login-password' && !passwordChangeRequired) setRoute('dashboard');
  }, [authLoading, authUser, passwordChangeRequired, route, userRole]);

  const signIn = async (email: string, password: string) => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
  };
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  };
  // Scroll to an in-home section anchor; if on another route, switch to home first.
  const goAnchor = (anchor: string) => {
    setPendingAnchor(anchor);
    if (route !== 'home') setRoute('home');
  };
  const goHome = () => goAnchor('home');
  const goPhotos = () => {
    setPendingAnchor(null);
    setRoute('photos');
  };
  const goToLogin = () => {
    setPendingAnchor(null);
    setAuthError(null);
    setRoute('login');
    window.history.replaceState({}, '', '/');
  };
  const goToForgotPassword = () => {
    setAuthError(null);
    setRoute('forgot-password');
    window.history.pushState({}, '', '/forgot-password');
  };
  useEffect(() => {
    const handlePopState = () => {
      const authRoute = getAuthRouteFromPath();
      if (authRoute) {
        setRoute(authRoute);
      } else {
        setRoute(authUser ? (passwordChangeRequired ? 'first-login-password' : 'dashboard') : 'home');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [authUser, passwordChangeRequired, userRole]);
  const goTestimonials = () => goAnchor('testimonials');

  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Navbar
        onAnchor={goAnchor}
        onTestimonials={goTestimonials}
        onPhotos={goPhotos}
        onLogin={goToLogin}
        activeSection={activeSection}
      />

      {/* <ConstructionPage mode="preview" onClose={dismissConstruction} /> */}

      {route === 'home' && (
        <>
          <Hero onStart={() => goToLogin()} onQuestionnaire={() => setQuestionnaireOpen(true)} />

          {/* Proof Strip — inverted triangle on sm+: 3 on top, 2 nestled in the gaps */}
          <section className="bg-mainBlue py-6 px-4">
            <div className="max-w-6xl mx-auto flex flex-col items-center gap-y-4 text-center text-white text-base md:text-lg font-medium lg:text-xl lg:font-bold lg:gap-y-5">
              {/* Top row — 3 evenly spaced (stacks on mobile) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 w-full gap-y-3 sm:gap-x-4 lg:gap-x-10">
                <span className="px-2">18 months–18 years+</span>
                <span className="px-2">3:1 student-to-tutor ratio</span>
                <span className="px-2">Take-home materials included</span>
              </div>
              {/* Bottom row — 2 items, center-anchored below the gaps of the top 3 */}
              <div className="w-full flex flex-col sm:flex-row md:flex-row items-center justify-center gap-x-24 md:gap-x-40 lg:gap-x-52 gap-y-3 sm:px-[16.67%] sm:mt-2">
                <span className="px-2 flex-shrink-0 whitespace-nowrap">Parent portal</span>
                <span className="px-2 flex-shrink-0 whitespace-nowrap">In-Person (all ages) + Online (7+)</span>
              </div>
            </div>
          </section>

          <Section title="Programs" bgColor="bg-white" contentClassName="w-full max-w-6xl mx-auto text-gray-700 text-lg">
            <div id="programs" className="w-full mx-auto scroll-mt-20 py-8 sm:py-12">
              <p className="mb-8 text-center text-gray-700 text-lg">
                Groups are based on formal cognitive stages of development rather than created age ranges. Since every child develops differently, placement within the program is flexible and tailored to each learner.
              </p>
              <button
                type="button"
                onClick={() => setImageModalOpen(true)}
                aria-label="View ETA Learning Program Packages in fullscreen"
                className="group block mx-auto w-full max-w-6xl cursor-pointer"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/ETA 2027 Packages.webp`}
                  alt="ETA Learning Program Packages"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-auto rounded-2xl shadow-xl ring-1 ring-black/5 transition-all duration-300 ease-out group-hover:-translate-y-1.5 group-hover:scale-[1.01] group-hover:shadow-2xl hover:scale-[1.005]"
                />
              </button>
              <a
                href={`${process.env.PUBLIC_URL}/ETA%202027%20Packages.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex min-h-[44px] items-center justify-center rounded-full bg-mainPink px-6 py-3 text-sm font-semibold text-white transition hover:bg-mainBlue"
              >
                View 2027 Program Packages (PDF)
              </a>
            </div>
          </Section>

          <Programs />

          <Section title="Holistic and Personalized Curriculum" bgColor="bg-mainBlue/10">
            <p>
              Every child is unique. At ETA, we tailor and continually adapt each student&rsquo;s curriculum to their strengths, pace, and curiosity, advancing their learning as they grow.
            </p>
          </Section>
          <Section title="Behavioural Development" bgColor="bg-white">
            <p>
              We nurture academic and behavioural growth by tracking focus, initiative, time management, self-regulation, confidence, and independence&mdash; building lasting skills for success beyond the classroom.
            </p>
          </Section>
          <Section title="Hands-On and Take Home Materials" bgColor="bg-mainPink/10">
            <p>
              Our Montessori-inspired, hands-on learning and structured worksheets are complemented by take-home materials and homework, reinforcing concepts and accelerating learning through consistent practice.
            </p>
          </Section>

          {/* Testimonials anchored by #testimonials */}
          <div id="testimonials" className="scroll-mt-20">
            <Testimonials />
          </div>
          <FAQ />
          <div id="contact" className="mt-8 scroll-mt-20">
            <Contact />
          </div>
          <Footer onGo={goAnchor} onTestimonials={goTestimonials} onPhotos={goPhotos} onContact={() => goAnchor('contact')} />
        </>
      )}

      {route === 'login' && <Login onLogin={signIn} onForgotPassword={goToForgotPassword} onBack={goHome} error={authError} />}

      {route === 'forgot-password' && (
        <ForgotPassword onBack={goToLogin} />
      )}

      {route === 'reset-password' && (
        <ResetPassword
          recoverySessionReady={recoverySessionReady}
          checkingRecoverySession={authLoading}
          onRequestAnotherLink={goToForgotPassword}
          onGoToLogin={goToLogin}
          onPasswordChanged={() => setPasswordChangeRequired(false)}
        />
      )}

      {route === 'first-login-password' && authUser && userRole && passwordChangeRequired && (
        <FirstLoginPasswordChange
          onComplete={() => {
            setPasswordChangeRequired(false);
            setRoute('dashboard');
          }}
          onSignOut={signOut}
        />
      )}

      {route === 'dashboard' && authUser && userRole && (
        <Dashboard
          onBack={goHome}
          onSignOut={signOut}
          userRole={userRole}
          userId={authUser.id}
          profileName={profileName}
        />
      )}

      {route === 'photos' && (
        <>
          <LearningPhotos onBack={goHome} />
          <Footer onGo={goAnchor} onTestimonials={goTestimonials} onPhotos={goPhotos} onContact={() => goAnchor('contact')} />
        </>
      )}
      <Questionnaire open={questionnaireOpen} onClose={() => setQuestionnaireOpen(false)} />
      {route === 'home' && (
        <ImageModal
          src={`${process.env.PUBLIC_URL}/ETA 2027 Packages.webp`}
          alt="ETA Learning Program Packages"
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
        />
      )}
    </div>
  );
};

export default App;
